import { Button, Form, Input, Tabs } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Modules
import { LOCALES_FIELD_NAME } from '@/modules/Locales';

// Components
import { TextArea } from '@/ui';
import { StyledFieldLabel, StyledWrapper } from './LocalesFormFieldsStyles';

// Helpers
import { TFormValidatorRule } from '@/types';
import { getLocaleDisplayLabel } from '@/models/localization.model';
import useGetLocales from '../../hooks/useGetLocales';

type TLocaleTabsFormProps = {
  value?: Record<string, Record<string, any>>;
  onChange?: (value: Record<string, Record<string, any>>) => void;
  labelPrefix: string;
  ns?: string;
  textAreaFields?: string[];
  withoutRulesFields?: string[];
  name?: string;
  rules?: TFormValidatorRule[];
  /** Fields that show a "translate" button on the target locale tab. */
  translateFields?: string[];
  /** Locale tab on which translate buttons are shown (default: 'ru'). */
  translateLocale?: string;
  /** Source locale whose value is sent to the translate callback (default: 'uk'). */
  translateFromLocale?: string;
  /** Called when the user clicks a translate button. Returns the translated string. */
  onTranslate?: (field: string, text: string) => Promise<string>;
  /** Optional per-field/per-locale label suffix, e.g. field source. */
  fieldLabelSuffixes?: Partial<Record<string, Partial<Record<string, string>>>>;
};

const LocalesFormFields = ({
  value = {},
  onChange,
  labelPrefix,
  textAreaFields,
  withoutRulesFields,
  ns = 'locales',
  name = LOCALES_FIELD_NAME,
  rules,
  translateFields,
  translateLocale = 'ru',
  translateFromLocale = 'uk',
  onTranslate,
  fieldLabelSuffixes,
}: TLocaleTabsFormProps) => {
  const locales = useGetLocales() ?? [];
  const [activeTab, setActiveTab] = useState<string>(locales[0]);
  const [translating, setTranslating] = useState<Record<string, boolean>>({});
  const { t } = useTranslation(['locales']);

  const allFields = Array.from(new Set(locales.flatMap((locale) => Object.keys(value?.[locale] || {}))));

  const handleFieldChange = (locale: string, field: string, fieldValue: any) => {
    const newValue = {
      ...value,
      [locale]: {
        ...value?.[locale],
        [field]: fieldValue,
      },
    };
    onChange?.(newValue);
  };

  const handleTranslate = async (field: string) => {
    if (!onTranslate) return;

    const sourceText: string = value?.[translateFromLocale]?.[field] ?? '';

    if (!sourceText) return;

    setTranslating((prev) => ({ ...prev, [field]: true }));

    try {
      const translated = await onTranslate(field, sourceText);

      if (translated) {
        handleFieldChange(translateLocale, field, translated);
      }
    } finally {
      setTranslating((prev) => ({ ...prev, [field]: false }));
    }
  };

  return (
    <StyledWrapper>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={locales.map((locale) => ({
          key: locale,
          label: getLocaleDisplayLabel(locale),
          forceRender: true,
          children: allFields.map((field) => {
            const isTextArea = textAreaFields?.includes(field);
            const showTranslateButton = locale === translateLocale && !!onTranslate && translateFields?.includes(field);
            const sourceText: string = value?.[translateFromLocale]?.[field] ?? '';
            const baseLabel = t(`${labelPrefix}${field}_label`, { ns });
            const suffix = fieldLabelSuffixes?.[field]?.[locale];
            const fieldLabel = suffix ? `${baseLabel} (${suffix})` : baseLabel;

            return (
              <Form.Item
                key={`${locale}-${field}`}
                name={[name, locale, field]}
                label={
                  showTranslateButton ? (
                    <StyledFieldLabel>
                      <span>{fieldLabel}</span>
                      <Button
                        size="small"
                        type="link"
                        loading={translating[field]}
                        disabled={!sourceText}
                        onClick={() => handleTranslate(field)}
                      >
                        {t('product_translate_field_button', { ns })}
                      </Button>
                    </StyledFieldLabel>
                  ) : (
                    fieldLabel
                  )
                }
                rules={withoutRulesFields?.includes(field) ? undefined : rules}
              >
                {isTextArea ? (
                  <TextArea onChange={(e) => handleFieldChange(locale, field, e.target.value)} />
                ) : (
                  <Input onChange={(e) => handleFieldChange(locale, field, e.target.value)} />
                )}
              </Form.Item>
            );
          }),
        }))}
      />
    </StyledWrapper>
  );
};

export default LocalesFormFields;
